import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useId, useRef, useState } from 'react';
import { remunerationMoney } from './RemunerationColumn';

/** Moyenne mensuelle du but annuel arrondie au centime (et non tronquée). */
export function monthlyAverageCents(annualCents: string): string {
  return ((BigInt(annualCents) + 6n) / 12n).toString();
}

function parseEuroAmount(draft: string): string | null {
  if (!/^\d+(?:[.,]\d{1,2})?$/u.test(draft)) return null;
  const [euros = '0', decimals = ''] = draft.replace(',', '.').split('.');
  const cents = BigInt(euros) * 100n + BigInt(decimals.padEnd(2, '0'));
  return cents >= 100n && cents <= 1000000000n ? cents.toString() : null;
}

export function WorkingCapitalSettingsDialog({
  annualGoalCents,
  suggestedReserveCents,
  onSave,
  onCancel,
}: {
  readonly annualGoalCents: string | null;
  readonly suggestedReserveCents: string | null;
  readonly onSave: (annualGoalCents: string) => Promise<void>;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [draft, setDraft] = useState(
    annualGoalCents === null ? '' : (Number(annualGoalCents) / 100).toFixed(2),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseEuroAmount(draft);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  async function submit(): Promise<void> {
    if (cents === null) {
      setError('Indiquez un objectif annuel positif en euros (deux décimales maximum).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(cents);
      onCancel();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalDialog
      ref={dialog}
      className="treasury-settings working-capital-settings"
      aria-labelledby={titleId}
      onCancel={(event) => {
        if (saving) event.preventDefault();
        else onCancel();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          onClick={onCancel}
          disabled={saving}
          aria-label="Fermer"
        />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <header className="tosho-modal-header">
          <h2 id={titleId}>Objectif de fond de roulement</h2>
        </header>
        <p>
          Paramétrez une cible annuelle. Le montant mensuel correspondant est calculé
          automatiquement. Aucun mouvement bancaire ni aucun solde réel n'est modifié.
        </p>
        <div className="working-capital-settings__advice">
          <strong>Conseil de Tosho</strong>
          <p>
            Prévoyez une progression régulière compatible avec vos dépenses professionnelles et
            votre réserve de sécurité. La carte suit le cumul annuel des entrées moins les sorties
            importées, rémunération déjà déduite, par rapport à cet objectif. Ce n’est ni le solde
            disponible du compte ni un fonds de roulement comptable.
          </p>
          {suggestedReserveCents !== null ? (
            <p>
              Repère distinct : la réserve de trésorerie conseillée est de{' '}
              <strong>{remunerationMoney(suggestedReserveCents)}</strong>. Elle ne constitue pas, à
              elle seule, un fond de roulement calculé.
            </p>
          ) : null}
        </div>
        <div className="working-capital-settings__row">
          <label htmlFor="working-capital-annual-goal">Objectif annuel</label>
          <div className="working-capital-settings__field">
            <input
              id="working-capital-annual-goal"
              type="text"
              inputMode="decimal"
              value={draft}
              onChange={(event) => {
                setDraft(event.currentTarget.value);
                setError(null);
              }}
              placeholder="Ex. : 12000,00"
              required
            />
            <span>€</span>
          </div>
        </div>
        <div className="working-capital-settings__row">
          <span>Objectif mensuel qui en découle</span>
          <strong>{cents === null ? '—' : remunerationMoney(monthlyAverageCents(cents))}</strong>
        </div>
        <p className="working-capital-settings__note">
          Moyenne mensuelle indicative, arrondie au centime ; la cible annuelle reste la référence.
          Cette cible ne certifie pas le fond de roulement effectivement constitué.
        </p>
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <footer>
          <button type="button" disabled={saving} onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" disabled={saving || cents === null}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
